import { LocalDate } from "@js-joda/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { BodyOf, Messages, ResponseOf } from "../../../../../core/api";
import { auth } from "../../../../../core/auth";
import useGraphQLQuery from "../../../../../shared/hooks/useGraphQLQuery";
import { GET_AGENCY_BRANCHES_FOR_FILTER_QUERY } from "../../../../caregiver/caregiver.graphql";
import useApi from "../../../../../shared/hooks/useApi";
import { phoneFormatter } from "../../../../../shared/utils/phone-formatter";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { CaregiverProfileBridgeActions } from "../../../caregiverProfileModal.actions";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";
import { getCaregiverFullName } from "../../../caregiverProfileModal.utils";

export type CaregiverAboutField =
  | "address2"
  | "applicationStartDate"
  | "authenticareWorkerId"
  | "birthDateDate"
  | "branchIds"
  | "certifications"
  | "email"
  | "employmentType"
  | "externalId"
  | "firstName"
  | "gender"
  | "hireDate"
  | "hcwId"
  | "homePhone"
  | "isClockEnabled"
  | "isTestUser"
  | "languages"
  | "lastName"
  | "lastWorkDate"
  | "mainLanguage"
  | "middleName"
  | "officeBranchIds"
  | "officeIds"
  | "phoneNumber"
  | "rehireDate"
  | "ssn"
  | "stateRegistryNum"
  | "terminationDate"
  | "firstWorkDate";

type CaregiverAboutEditableCaregiver = CaregiverProfileCaregiver & {
  authenticareWorkerId?: string | null;
  employmentType?: string | null;
  isEmailVerified?: boolean | null;
  isTestUser?: boolean;
};

export type CaregiverAboutDraft = Pick<
  CaregiverProfileCaregiver,
  | "address2"
  | "applicationStartDate"
  | "birthDateDate"
  | "branchIds"
  | "certifications"
  | "email"
  | "firstName"
  | "gender"
  | "hireDate"
  | "hcwId"
  | "homePhone"
  | "isClockEnabled"
  | "languages"
  | "lastName"
  | "lastWorkDate"
  | "mainLanguage"
  | "middleName"
  | "officeBranchIds"
  | "officeIds"
  | "phoneNumber"
  | "rehireDate"
  | "ssn"
  | "stateRegistryNum"
  | "terminationDate"
  | "firstWorkDate"
> & {
  authenticareWorkerId: string | null;
  employmentType: string | null;
  externalId: string | null;
  isTestUser: boolean;
};

type CaregiverPatchBody = BodyOf<"patch", "./caregiver/:caregiverId">;
type CaregiverOfficesBody = BodyOf<
  "put",
  "/agencies/:agencyId/offices/agency_members/:agencyMemberId/assoc_caregivers"
>;
type CaregiverAgencyAssocBody = BodyOf<"put", "./caregivers/:caregiverId/agency_assoc">;
type CaregiverTerminationDateBody = BodyOf<"put", "./caregivers/:caregiverId/termination_date">;
type CaregiverHireDateBody = BodyOf<"put", "./caregivers/:caregiverId/hire_date">;
type CaregiverClockEnabledBody = BodyOf<"put", "./caregivers/:caregiverId/enable_clock_in">;
type CaregiverCertificationsBody = BodyOf<"put", "./caregivers/:caregiverId/certifications">;
type CaregiverOfficeBranchesBody = BodyOf<"put", "./caregivers/:caregiverId/office_branches">;
type CaregiverBranchesBody = BodyOf<"put", "./caregivers/:caregiverId/branches">;
type CaregiverFlagsResponse = ResponseOf<"get", "./caregiver/:caregiverId/flags">;
type CaregiverFlagOption = Messages["CaregiverFlagOption"];
type CaregiverFlagBody = BodyOf<"patch", "./caregiver/:caregiverId/flags">;
type CaregiverUnblockLoginResponse = ResponseOf<
  "get",
  "/agencies/:agencyId/coordinator/:agencyMemberId/caregivers/:caregiverId/show_unblock_login"
>;

function createDraft(caregiver: CaregiverProfileCaregiver): CaregiverAboutDraft {
  const editableCaregiver = caregiver as CaregiverAboutEditableCaregiver;

  return {
    address2: caregiver.address2,
    applicationStartDate: caregiver.applicationStartDate,
    authenticareWorkerId: editableCaregiver.authenticareWorkerId ?? null,
    birthDateDate: caregiver.birthDateDate,
    branchIds: caregiver.branchIds,
    certifications: caregiver.certifications,
    email: caregiver.email,
    employmentType: editableCaregiver.employmentType ?? null,
    externalId: caregiver.externalId ?? null,
    firstName: caregiver.firstName,
    gender: caregiver.gender,
    hireDate: caregiver.hireDate,
    hcwId: caregiver.hcwId ?? null,
    homePhone: caregiver.homePhone,
    isClockEnabled: caregiver.isClockEnabled,
    isTestUser: editableCaregiver.isTestUser ?? false,
    languages: caregiver.languages,
    lastName: caregiver.lastName,
    lastWorkDate: caregiver.lastWorkDate,
    mainLanguage: caregiver.mainLanguage,
    middleName: caregiver.middleName,
    officeBranchIds: caregiver.officeBranchIds ?? [],
    officeIds: caregiver.officeIds,
    phoneNumber: caregiver.phoneNumber,
    rehireDate: caregiver.rehireDate,
    ssn: caregiver.ssn,
    stateRegistryNum: caregiver.stateRegistryNum,
    terminationDate: caregiver.terminationDate,
    firstWorkDate: caregiver.firstWorkDate,
  };
}

function normalizeDraftValue<TKey extends keyof CaregiverAboutDraft>(
  key: TKey,
  value: CaregiverAboutDraft[TKey]
) {
  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (
      [
        "address2",
        "authenticareWorkerId",
        "email",
        "employmentType",
        "externalId",
        "hcwId",
        "homePhone",
        "middleName",
        "ssn",
      ].includes(key)
    ) {
      return trimmedValue === "" ? null : trimmedValue;
    }

    if (key === "homePhone" || key === "phoneNumber") {
      return phoneFormatter.isValidNumber(trimmedValue)
        ? phoneFormatter.formatE164(trimmedValue)
        : trimmedValue;
    }

    return trimmedValue;
  }

  return value;
}

function toPatchBody(field: CaregiverAboutField, draft: CaregiverAboutDraft): CaregiverPatchBody {
  const value = normalizeDraftValue(field, draft[field]);

  if (
    field === "birthDateDate" ||
    field === "hireDate" ||
    field === "rehireDate" ||
    field === "terminationDate" ||
    field === "applicationStartDate" ||
    field === "firstWorkDate" ||
    field === "lastWorkDate"
  ) {
    return { [field]: value === null ? null : (value as LocalDate) };
  }

  return { [field]: value };
}

function isAgencyAssocField(field: CaregiverAboutField) {
  return (
    field === "applicationStartDate" ||
    field === "employmentType" ||
    field === "firstWorkDate" ||
    field === "lastWorkDate"
  );
}

function isTerminationDateField(field: CaregiverAboutField) {
  return field === "terminationDate";
}

function isHireDateField(field: CaregiverAboutField) {
  return field === "hireDate";
}

function isClockEnabledField(field: CaregiverAboutField) {
  return field === "isClockEnabled";
}

function normalizePhoneNumberForApi(phoneNumber: string) {
  const trimmedPhoneNumber = phoneNumber.trim();

  if (trimmedPhoneNumber === "") {
    return "";
  }

  if (phoneFormatter.isValidNumber(trimmedPhoneNumber)) {
    return phoneFormatter.formatE164(trimmedPhoneNumber);
  }

  return trimmedPhoneNumber.startsWith("+") ? trimmedPhoneNumber : `+1${trimmedPhoneNumber}`;
}

export function useCaregiverProfileAboutController(
  caregiver: CaregiverProfileCaregiver,
  actions: CaregiverProfileBridgeActions
) {
  const { api, queries } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const authState = auth.getAuthOrFail();
  const agencyId = authState.data.agency.id;
  const agencyMemberOfficeIds = authState.data.agencyMember.officeIds;
  const isOnboardingAgency = authState.isMedflytAgencyMember;
  const canEdit = auth.isAllowedTo("edit_caregiver_page_about_details");
  const canChangeStatus = auth.isAllowedTo("edit_caregiver_status");
  const canEditSsn = auth.isAllowedTo("show_caregiver_ssn");
  const canEditTestUser = auth.isAllowedTo("edit_caregiver_is_test_user");
  const [isEditing, setIsEditing] = React.useState(false);
  const [activeField, setActiveField] = React.useState<CaregiverAboutField | null>(null);
  const [draft, setDraft] = React.useState<CaregiverAboutDraft>(() => createDraft(caregiver));
  const [isAddingPhoneNumber, setIsAddingPhoneNumber] = React.useState(false);
  const [phoneNumberDraft, setPhoneNumberDraft] = React.useState("");
  const createdByIds = caregiverWithIds(caregiver);
  const caregiverQueryKey = queries.caregiver.get(caregiver.id).queryKey;
  const certificationsQuery = useQuery({
    ...queries.caregiver.certifications(),
    enabled: isEditing || activeField === "certifications",
  });
  const officesQuery = useQuery({
    ...queries.office.list(),
  });
  const branchesQuery = useGraphQLQuery({ document: GET_AGENCY_BRANCHES_FOR_FILTER_QUERY });
  const officeBranchesQuery = useQuery(queries.officeBranch.list());
  const agencyFeaturesQuery = useQuery({
    queryKey: ["agency", agencyId, "features"],
    queryFn: () =>
      api.anyRequest<Record<string, boolean>>("GET", "/agencies/:agencyId/features", {}),
  });
  const caregiverFlagsQuery = useQuery({
    queryKey: ["caregiverProfileModal", caregiver.id, "flags"],
    queryFn: () =>
      api.get("./caregiver/:caregiverId/flags", {
        path: { caregiverId: caregiver.id },
      }),
  });
  const agencyMembersQuery = useQuery({
    ...queries.agencyMember.list(),
    enabled:
      createdByIds.agencyAssocCreatedByAgencyMemberId !== null &&
      createdByIds.agencyAssocCreatedByAgencyMemberId !== undefined,
  });
  const createdByCaregiverQuery = useQuery({
    ...queries.caregiver.get(createdByIds.agencyAssocCreatedByCaregiverId ?? caregiver.id),
    enabled:
      createdByIds.agencyAssocCreatedByCaregiverId !== null &&
      createdByIds.agencyAssocCreatedByCaregiverId !== undefined,
  });
  const unblockLoginQuery = useQuery({
    queryKey: ["caregiverProfileModal", caregiver.id, "showUnblockLogin"],
    queryFn: () =>
      api.get(
        "/agencies/:agencyId/coordinator/:agencyMemberId/caregivers/:caregiverId/show_unblock_login",
        { path: { caregiverId: caregiver.id } }
      ),
  });

  React.useEffect(() => {
    if (isEditing) {
      return;
    }

    setDraft(createDraft(caregiver));
  }, [caregiver, isEditing]);

  const patchCaregiverMutation = useMutation({
    mutationFn: ({ body, field }: { body: CaregiverPatchBody; field: CaregiverAboutField }) => {
      if (isAgencyAssocField(field)) {
        return api.put("./caregivers/:caregiverId/agency_assoc", {
          path: { caregiverId: caregiver.id },
          body: body as CaregiverAgencyAssocBody,
        });
      }

      if (isTerminationDateField(field)) {
        return api.put("./caregivers/:caregiverId/termination_date", {
          path: { caregiverId: caregiver.id },
          body: body as CaregiverTerminationDateBody,
        });
      }

      if (isHireDateField(field)) {
        return api.put("./caregivers/:caregiverId/hire_date", {
          path: { caregiverId: caregiver.id },
          body: body as CaregiverHireDateBody,
        });
      }

      if (isClockEnabledField(field)) {
        return api.put("./caregivers/:caregiverId/enable_clock_in", {
          path: { caregiverId: caregiver.id },
          body: body as CaregiverClockEnabledBody,
        });
      }

      return api.patch("./caregiver/:caregiverId", {
        path: { caregiverId: caregiver.id },
        body,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(caregiverQueryKey, (currentCaregiver) => {
        if (currentCaregiver === undefined) {
          return currentCaregiver;
        }

        return {
          ...currentCaregiver,
          [variables.field]: variables.body[variables.field as keyof CaregiverPatchBody],
        };
      });
      setActiveField(null);
      toast({ description: "Caregiver updated successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not update caregiver.", status: "error" });
    },
  });
  const updateCertificationsMutation = useMutation({
    mutationFn: (body: CaregiverCertificationsBody) =>
      api.put("./caregivers/:caregiverId/certifications", {
        path: { caregiverId: caregiver.id },
        body,
      }),
    onSuccess: (_, body) => {
      queryClient.setQueryData(caregiverQueryKey, (currentCaregiver) => {
        if (currentCaregiver === undefined) {
          return currentCaregiver;
        }

        return { ...currentCaregiver, certifications: body.certifications };
      });
      setActiveField(null);
      toast({ description: "Caregiver certifications updated successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not update caregiver certifications.", status: "error" });
    },
  });
  const updateBranchesMutation = useMutation({
    mutationFn: (branchIds: CaregiverProfileCaregiver["branchIds"]) =>
      api.put("./caregivers/:caregiverId/branches", {
        path: { caregiverId: caregiver.id },
        body: { branchIds } satisfies CaregiverBranchesBody,
      }),
    onSuccess: (_, branchIds) => {
      queryClient.setQueryData(caregiverQueryKey, (currentCaregiver) => {
        if (currentCaregiver === undefined) {
          return currentCaregiver;
        }

        return { ...currentCaregiver, branchIds };
      });
      setActiveField(null);
      toast({ description: "Caregiver branches updated successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not update caregiver branches.", status: "error" });
    },
  });
  const updateOfficeBranchesMutation = useMutation({
    mutationFn: (officeBranchIds: CaregiverOfficeBranchesBody["officeBranchIds"]) =>
      api.put("./caregivers/:caregiverId/office_branches", {
        path: { caregiverId: caregiver.id },
        body: { officeBranchIds } satisfies CaregiverOfficeBranchesBody,
      }),
    onSuccess: (_, officeBranchIds) => {
      queryClient.setQueryData(caregiverQueryKey, (currentCaregiver) =>
        currentCaregiver === undefined ? currentCaregiver : { ...currentCaregiver, officeBranchIds }
      );
      setActiveField(null);
      toast({ description: "Caregiver office branches updated successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not update caregiver office branches.", status: "error" });
    },
  });
  const updateOfficesMutation = useMutation({
    mutationFn: (officeIds: CaregiverProfileCaregiver["officeIds"]) => {
      const currentOfficeIds = caregiver.officeIds;
      const currentOfficeIdSet = new Set(currentOfficeIds);
      const nextOfficeIdSet = new Set(officeIds);
      const associations: CaregiverOfficesBody["associations"] = [
        ...officeIds
          .filter((officeId) => !currentOfficeIdSet.has(officeId))
          .map((officeId) => ({
            caregiverId: caregiver.id,
            officeId,
            type: "ASSOCIATE" as const,
          })),
        ...currentOfficeIds
          .filter((officeId) => !nextOfficeIdSet.has(officeId))
          .map((officeId) => ({
            caregiverId: caregiver.id,
            officeId,
            type: "DISSOCIATE" as const,
          })),
      ];

      if (associations.length === 0) {
        return Promise.resolve();
      }

      return api.put(
        "/agencies/:agencyId/offices/agency_members/:agencyMemberId/assoc_caregivers",
        {
          body: { associations },
        }
      );
    },
    onSuccess: async (_, officeIds) => {
      queryClient.setQueryData(caregiverQueryKey, (currentCaregiver) => {
        if (currentCaregiver === undefined) {
          return currentCaregiver;
        }

        return { ...currentCaregiver, officeIds };
      });
      setActiveField(null);
      await queryClient.invalidateQueries({ queryKey: caregiverQueryKey });
      toast({ description: "Caregiver offices updated successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not update caregiver offices.", status: "error" });
    },
  });
  const addPhoneNumberMutation = useMutation({
    mutationFn: (phoneNumber: string) =>
      api.put("/agencies/:agencyId/caregivers/:caregiverId/phone_number", {
        path: { caregiverId: caregiver.id },
        body: { phoneNumber },
      }),
    onSuccess: (response, phoneNumber) => {
      const phoneNumberType: CaregiverProfileCaregiver["phoneNumbers"][number]["type"] =
        response?.phoneNumberSmsable === true ? "mobile" : "landline";

      queryClient.setQueryData(caregiverQueryKey, (currentCaregiver) => {
        if (currentCaregiver === undefined) {
          return currentCaregiver;
        }

        return {
          ...currentCaregiver,
          phoneNumbers: [
            ...currentCaregiver.phoneNumbers,
            {
              phoneNumber,
              type: phoneNumberType,
            },
          ],
        };
      });
      setIsAddingPhoneNumber(false);
      setPhoneNumberDraft("");
      toast({ description: "Phone number added successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not add phone number.", status: "error" });
    },
  });
  const deletePhoneNumberMutation = useMutation({
    mutationFn: (phoneNumber: CaregiverProfileCaregiver["phoneNumbers"][number]) =>
      api.delete("/agencies/:agencyId/caregivers/:caregiverId/phone_number/:phoneNumber", {
        path: {
          caregiverId: caregiver.id,
          phoneNumber: phoneNumber.phoneNumber,
        },
      }),
    onSuccess: (_, phoneNumber) => {
      queryClient.setQueryData(caregiverQueryKey, (currentCaregiver) => {
        if (currentCaregiver === undefined) {
          return currentCaregiver;
        }

        return {
          ...currentCaregiver,
          phoneNumbers: currentCaregiver.phoneNumbers.filter(
            (item) => item.phoneNumber !== phoneNumber.phoneNumber
          ),
        };
      });
      toast({ description: "Phone number deleted successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not delete phone number.", status: "error" });
    },
  });
  const updateFlagMutation = useMutation({
    mutationFn: (params: { flag: CaregiverFlagOption; active: boolean }) =>
      api.patch("./caregiver/:caregiverId/flags", {
        path: { caregiverId: caregiver.id },
        body: params satisfies CaregiverFlagBody,
      }),
    onSuccess: (_, params) => {
      queryClient.setQueryData<CaregiverFlagsResponse>(
        ["caregiverProfileModal", caregiver.id, "flags"],
        (currentFlags) => {
          if (currentFlags === undefined) {
            return currentFlags;
          }

          return {
            flags: {
              ...currentFlags.flags,
              [params.flag]: params.active,
            },
          };
        }
      );
      toast({ description: "Caregiver flags updated successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not update caregiver flags.", status: "error" });
    },
  });

  const unblockLogin = () => {
    actions.unblockLogin({
      caregiver,
      onDone: () => {
        queryClient.setQueryData<CaregiverUnblockLoginResponse>(
          ["caregiverProfileModal", caregiver.id, "showUnblockLogin"],
          { show: false }
        );
        toast({ description: "Caregiver login unblocked successfully.", status: "success" });
      },
    });
  };

  const updateDraft = <TKey extends keyof CaregiverAboutDraft>(
    key: TKey,
    value: CaregiverAboutDraft[TKey]
  ) => setDraft((currentDraft) => ({ ...currentDraft, [key]: value }));

  const cancelField = () => {
    setDraft(createDraft(caregiver));
    setActiveField(null);
  };

  const commitField = (field: CaregiverAboutField) => {
    if (!canEdit) {
      return;
    }

    if (field === "officeBranchIds") {
      const eligibleIds = new Set(
        (officeBranchesQuery.data?.list ?? [])
          .filter((branch) => caregiver.officeIds.includes(branch.officeId))
          .map((branch) => branch.id)
      );
      if (draft.officeBranchIds.some((id) => !eligibleIds.has(id))) {
        toast({
          description: "Select branches belonging to the caregiver's offices.",
          status: "error",
        });
        return;
      }
      updateOfficeBranchesMutation.mutate(draft.officeBranchIds);
      return;
    }
    if (field === "certifications") {
      if (draft.certifications.length === 0) {
        toast({
          description: "Caregiver must have at least one certification.",
          status: "error",
        });
        return;
      }

      updateCertificationsMutation.mutate({ certifications: draft.certifications });
      return;
    }

    if (field === "branchIds") {
      updateBranchesMutation.mutate(draft.branchIds);
      return;
    }

    if (field === "officeIds") {
      if (draft.officeIds.length === 0) {
        toast({
          description: "Caregiver must have at least 1 office associated.",
          status: "error",
        });
        return;
      }

      const changedOfficeIds = [
        ...draft.officeIds.filter((officeId) => !caregiver.officeIds.includes(officeId)),
        ...caregiver.officeIds.filter((officeId) => !draft.officeIds.includes(officeId)),
      ];
      const hasInaccessibleOfficeChange = changedOfficeIds.some(
        (officeId) => !agencyMemberOfficeIds.includes(officeId)
      );

      if (hasInaccessibleOfficeChange) {
        toast({
          description: "You can only edit offices you have access to.",
          status: "error",
        });
        return;
      }

      updateOfficesMutation.mutate(draft.officeIds);
      return;
    }

    if (field === "ssn") {
      const normalizedSsn = draft.ssn?.trim() ?? "";
      const isValidSsn =
        normalizedSsn === "" ||
        /^\d{9}$/.test(normalizedSsn) ||
        /^\d{3}-\d{2}-\d{4}$/.test(normalizedSsn);

      if (!isValidSsn) {
        toast({ description: "Invalid SSN.", status: "error" });
        return;
      }
    }

    patchCaregiverMutation.mutate({ body: toPatchBody(field, draft), field });
  };

  const addPhoneNumber = () => {
    const normalizedPhoneNumber = normalizePhoneNumberForApi(phoneNumberDraft);

    if (normalizedPhoneNumber === "") {
      return;
    }

    addPhoneNumberMutation.mutate(normalizedPhoneNumber);
  };

  const deletePhoneNumber = (phoneNumber: CaregiverProfileCaregiver["phoneNumbers"][number]) => {
    const shouldDelete = window.confirm("Are you sure you want to remove this phone number?");

    if (!shouldDelete) {
      return;
    }

    deletePhoneNumberMutation.mutate(phoneNumber);
  };

  const openChangeStatus = () => {
    actions.openChangeStatus({
      caregiver,
      onDone: () => {
        queryClient.invalidateQueries({ queryKey: caregiverQueryKey });
        queryClient.invalidateQueries({
          queryKey: ["caregiverProfileModal", caregiver.id, "statusHistory"],
        });
      },
    });
  };
  const openExclusionLists = () => {
    actions.openExclusionLists({ caregiver });
  };

  return {
    activeField,
    addPhoneNumber,
    agencyId,
    canEdit,
    canChangeStatus,
    canEditSsn,
    canEditTestUser,
    cancelField,
    branches: branchesQuery.data?.agencyBranches.nodes ?? [],
    officeBranches: (officeBranchesQuery.data?.list ?? []).filter((branch) =>
      caregiver.officeIds.includes(branch.officeId)
    ),
    isLoadingBranches: branchesQuery.isPending || branchesQuery.isError,
    isLoadingOfficeBranches: officeBranchesQuery.isPending || officeBranchesQuery.isError,
    showHcwIdFeature: agencyFeaturesQuery.data?.hcwIdEnabled === true,
    certifications: certificationsQuery.data ?? [],
    commitField,
    createdByName: getCreatedByName({
      caregiver,
      agencyMembers: agencyMembersQuery.data?.agencyMembers,
      createdByCaregiver: createdByCaregiverQuery.data,
    }),
    deletePhoneNumber,
    draft,
    flags: caregiverFlagsQuery.data?.flags ?? null,
    isDeletingPhoneNumber: deletePhoneNumberMutation.isPending,
    isAddingPhoneNumber,
    isEditing,
    isSaving:
      patchCaregiverMutation.isPending ||
      updateCertificationsMutation.isPending ||
      updateBranchesMutation.isPending ||
      updateOfficeBranchesMutation.isPending ||
      updateOfficesMutation.isPending ||
      addPhoneNumberMutation.isPending ||
      deletePhoneNumberMutation.isPending,
    isOnboardingAgency,
    isUpdatingFlag: updateFlagMutation.isPending,
    shouldShowUnblockLogin: unblockLoginQuery.data?.show === true,
    offices: officesQuery.data?.offices ?? [],
    openChangeStatus,
    openExclusionLists,
    phoneNumberDraft,
    setActiveField,
    setDraft,
    setIsAddingPhoneNumber,
    setIsEditing,
    setPhoneNumberDraft,
    unblockLogin,
    updateFlag: (flag: CaregiverFlagOption, active: boolean) =>
      updateFlagMutation.mutate({ flag, active }),
    updateDraft,
  };
}

function caregiverWithIds(caregiver: CaregiverProfileCaregiver) {
  return caregiver as CaregiverProfileCaregiver & {
    agencyAssocCreatedByAgencyMemberId?: number | null;
    agencyAssocCreatedByCaregiverId?: CaregiverProfileCaregiver["id"] | null;
  };
}

function getCreatedByName(params: {
  caregiver: CaregiverProfileCaregiver;
  agencyMembers?: Array<{ id: number; firstName: string; lastName: string }>;
  createdByCaregiver?: CaregiverProfileCaregiver;
}) {
  const caregiverActivityDetails = params.caregiver as CaregiverProfileCaregiver & {
    agencyAssocCreatedByAgencyMemberName?: string | null;
    agencyAssocCreatedByCaregiverName?: string | null;
    createdByName?: string | null;
  };
  const createdByIds = caregiverWithIds(params.caregiver);

  if (caregiverActivityDetails.createdByName) {
    return caregiverActivityDetails.createdByName;
  }

  if (caregiverActivityDetails.agencyAssocCreatedByAgencyMemberName) {
    return caregiverActivityDetails.agencyAssocCreatedByAgencyMemberName;
  }

  if (caregiverActivityDetails.agencyAssocCreatedByCaregiverName) {
    return caregiverActivityDetails.agencyAssocCreatedByCaregiverName;
  }

  const agencyMember = params.agencyMembers?.find(
    (item) => item.id === createdByIds.agencyAssocCreatedByAgencyMemberId
  );

  if (agencyMember !== undefined) {
    return `${agencyMember.firstName} ${agencyMember.lastName}`;
  }

  if (params.createdByCaregiver !== undefined) {
    return getCaregiverFullName(params.createdByCaregiver);
  }

  return null;
}
